import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import Locator from '../../../../../shared/Locator';
import { useDresser } from '../../../../contexts/DrawerContext';
import * as DependenciesV2Store from '../../../../redux/dependenciesV2';
import ProjectDependencyDrawer from '../DependencyDrawer/ProjectDependencyDrawer';
import ReleaseGroupDependencyDrawer from '../DependencyDrawer/ReleaseGroupDependencyDrawer';
import type { DependencyScope } from '../../../../../shared/dependenciesV2';

type DependencyDrawerOptions = {
  // the dependency scope
  scope: DependencyScope | null;
  // the currently selected dependency locator
  selectedDependencyRevisionLocator: string | null;
  // callback for setting/unsetting the currently selected dependency locator
  setSelectedDependencyRevisionLocator: (locator: string | null) => void;
  // optional callback when dependency is deleted/ignored
  onDeleteProjectDependency: (locator: string) => void;
};

/**
 * Helper for displaying and hiding the dependency drawer, used in both managed
 * and vendored dependency views
 * @param opts the dependency drawer options
 */
export function useDependencyDrawer(opts: DependencyDrawerOptions) {
  const { scope, selectedDependencyRevisionLocator } = opts;
  const { setSelectedDependencyRevisionLocator, onDeleteProjectDependency } = opts;
  const { popDrawer, replaceDrawer } = useDresser();
  const revisionScanId = useSelector(DependenciesV2Store.selectors.getRevisionScanId);

  const onClose = useCallback(() => {
    setSelectedDependencyRevisionLocator(null);
  }, [setSelectedDependencyRevisionLocator]);

  useEffect(() => {
    if (selectedDependencyRevisionLocator) {
      if (scope?.scope === 'releaseGroup') {
        replaceDrawer(
          <ReleaseGroupDependencyDrawer
            onClose={onClose}
            releaseGroupId={scope.projectGroupId}
            releaseGroupReleaseId={scope.projectGroupReleaseId}
            dependencyRevisionLocator={selectedDependencyRevisionLocator}
            scope={{
              type: 'releaseGroup',
              releaseGroupId: scope.projectGroupId,
              releaseId: scope.projectGroupReleaseId,
            }}
          />,
          { onClose, shouldSkipClickOutside: true },
        );
      } else if (scope?.scope === 'project') {
        replaceDrawer(
          <ProjectDependencyDrawer
            onClose={onClose}
            projectRevisionLocator={scope.revision}
            dependencyRevisionLocator={selectedDependencyRevisionLocator}
            revisionScanId={revisionScanId}
            onDeleteDependency={onDeleteProjectDependency}
            scope={{
              type: 'project',
              projectLocator: Locator.parsePackageString(scope.revision),
              revisionLocator: scope.revision,
            }}
          />,
          { onClose, shouldSkipClickOutside: true },
        );
      }
    }
  }, [
    selectedDependencyRevisionLocator,
    scope,
    revisionScanId,
    replaceDrawer,
    onDeleteProjectDependency,
    popDrawer,
    onClose,
  ]);
}
